/**
 * The WIRE BOUNDARY: `live` → the parameters patch, in one pass.
 *
 * This was two modules — a generic differ that knew key presence but held no contract, and a sanitizer that saw the differ's nulls but had lost what produced them. Neither half can build a correct patch alone: the differ can only say "this key vanished", and by the time the sanitizer sees the null it can no longer tell a REMOVED CONTAINER from a PRUNED PARAM. So the first step manufactured an artifact and the second undid it. Here every decision is made AT a node, with the contract in hand.
 *
 * THE RULES, one per level:
 *
 * - Descend through CONTAINERS (the effect map, `objects`, an object instance), stop at PARAMS.
 * - A PARAM IS A LEAF whatever its value looks like — scalar, string, array, or a morph envelope. Its internal shape is the contract's business, never the walk's. This is what keeps a mid-morph tick emitting the WHOLE `{a,b,t,f}` rather than the changed `t`, and a changed `children` emitting the whole array.
 * - A vanished CONTAINER emits `null` — the removal signal, the only null that carries information no ordinary value could.
 * - A vanished PARAM emits its contract `initial` — it is not gone, it is at its default.
 *
 * ⚠️ NO PARAM-LEVEL NULL EVER REACHES THE WIRE. At the param level `null` and the initial are the same statement (a receiver told `null`-then-resolve and one told `50` end identically), so emitting the value directly removes a whole class of question from the server.
 *
 * ⚠️ STRUCTURAL POSITION IS THE ONLY DISCRIMINATOR, never value shape: `fill` is `string|object`, `children` is an array, `advanced` is a real nested sub-dict the server deep-merges, and a morphing `prompt` is an object. `objects` also nests one level deeper than an effect. So the walk branches on WHICH subtree it is in — never on `typeof`, never on a depth counter.
 *
 * NO scale conversion happens here. The wire carries the same 0-100 values `live` holds and presets store; the server converts on its own side (normalization-map.md §Decision 2026-08-02).
 */
import { classifyParam, getObjectInitial } from '~/lib/params'
import { EFFECT_SCHEMAS, WIRE_KEYS } from '~/schemas/effect_schemas'
import display_object_schema from '~/schemas/display_object_schema'

/** Deep equality for leaf values — a param may be an array or an envelope, and both must compare by value. */
const same = (a, b) => {
	if (a === b) return true
	if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false
	if (Array.isArray(a) !== Array.isArray(b)) return false
	const ak = Object.keys(a)
	const bk = Object.keys(b)
	if (ak.length !== bk.length) return false
	return ak.every(k => same(a[k], b[k]))
}

/** A whole-value copy, so nothing downstream can mutate `live` through the patch. */
const copyLeaf = v => (v !== null && typeof v === 'object' ? structuredClone(v) : v)

/**
 * Diff one PARAM MAP (an effect, or one object instance) — every value inside is a leaf.
 *
 * @param initialOf a key's initial, or undefined when the contract declares none
 * @param integerOf whether a key is an integer param, by contract type
 */
const diffParams = (from = {}, to = {}, initialOf, integerOf) => {
	const out = {}

	for (const key in to) {
		const next = to[key]
		if (same(from[key], next)) continue
		out[key] = integerOf?.(key) && typeof next === 'number' ? Math.round(next) : copyLeaf(next)
	}

	// A key present in FROM and gone from TO is a param at its initial — not a removal.
	for (const key in from) {
		if (key in to) continue
		const initial = initialOf(key)
		// An undeclared key has no initial to resolve to. Leaving it out is the only honest option: inventing a value would state something the contract does not.
		if (initial !== undefined) out[key] = initial
	}

	return out
}

const isInteger = props => key => classifyParam(props?.[key]).integer

const schemaInitial = props => key => props?.[key]?.initial

/** The object instance's own property map, resolved by its live `type`. `root` is identified by its id — stored roots carry `{id, children}` and no type. */
const propsForInstance = (id, instance) =>
	display_object_schema.definitions[id === 'root' ? 'root' : instance?.type]?.properties

const instanceInitial = (id, instance) =>
	id === 'root' ? schemaInitial(propsForInstance(id, instance)) : key => getObjectInitial(instance?.type, key)

/**
 * Diff the scene graph. One level deeper than an effect: `objects` is a container OF containers, so a vanished instance is a removal (`null`) while a vanished key inside one is a param at its initial.
 */
const diffObjects = (from = {}, to = {}) => {
	const out = {}

	for (const id in to) {
		const instance = to[id]
		if (!(id in from)) {
			out[id] = structuredClone(instance)
			continue
		}
		const props = propsForInstance(id, instance)
		const patch = diffParams(from[id], instance, instanceInitial(id, instance), isInteger(props))
		if (Object.keys(patch).length) out[id] = patch
	}

	// A vanished INSTANCE is a real removal — the sentinel the server pops.
	for (const id in from) if (!(id in to)) out[id] = null

	return out
}

/** Loop definitions by path: a changed def goes whole, a removed one as `null`. */
const diffModulators = (from = {}, to = {}) => {
	const out = {}
	for (const path in to) if (!same(from[path], to[path])) out[path] = structuredClone(to[path])
	for (const path in from) if (!(path in to)) out[path] = null
	return out
}

/** A param as the wire states it: absent and `null` are its initial, an integer param is rounded. */
const resolved = (v, prop, initial = prop?.initial) => {
	const value = v ?? initial
	return classifyParam(prop).integer && typeof value === 'number' ? Math.round(value) : value
}

const sameInstance = (id, a, b) => {
	const props = propsForInstance(id, a)
	const initialOf = instanceInitial(id, a)
	for (const key of new Set([...Object.keys(a), ...Object.keys(b)]))
		if (!same(resolved(a[key], props?.[key], initialOf(key)), resolved(b[key], props?.[key], initialOf(key))))
			return false
	return true
}

/** Do two sections agree on one merge unit, as the wire would state it? An absent effect is a removal; an absent param is its initial. */
export const sameUnit = (section, id, a, b) => {
	if (section === 'modulators') return same(a?.[id] ?? null, b?.[id] ?? null)
	if (section === 'objects') {
		const x = a?.[id] ?? null
		const y = b?.[id] ?? null
		if (x === null || y === null) return x === y
		return sameInstance(id, x, y)
	}
	if ((a ?? null) === null || (b ?? null) === null) return (a ?? null) === (b ?? null)
	const prop = EFFECT_SCHEMAS[section]?.properties?.[id]
	return same(resolved(a[id], prop), resolved(b[id], prop))
}

/**
 * Build the parameters patch.
 *
 * @param lastSent the live state the server already knows (`{}` for a full snapshot)
 * @param live current live state
 * @returns the patch; empty when nothing the wire carries has changed
 */
export function buildWirePatch(lastSent = {}, live = {}) {
	const patch = {}

	for (const key of WIRE_KEYS) {
		const from = lastSent[key]
		const to = live[key]

		if (to === undefined) {
			// A vanished CONTAINER — the removal signal. `objects` itself is never removed, only its instances.
			if (from !== undefined && key !== 'objects') patch[key] = null
			continue
		}

		if (key === 'objects') {
			const objects = diffObjects(from, to)
			if (Object.keys(objects).length) patch.objects = objects
			continue
		}

		const props = EFFECT_SCHEMAS[key]?.properties
		const effect = diffParams(from, to, schemaInitial(props), isInteger(props))
		// ⚠️ `{}` IS EMITTED when the effect is newly present — it asserts existence with every param at its initial, and dropping it as "empty" is what would make an added effect invisible to the peers.
		if (Object.keys(effect).length || from === undefined) patch[key] = effect
	}

	const modulators = diffModulators(lastSent.modulators, live.modulators)
	if (Object.keys(modulators).length) patch.modulators = modulators

	return patch
}

export default buildWirePatch
